import { API, links } from "@http";
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import { generalErrorHandler } from "../rootSaga";
import {
  setEmailsForSendPin,
  getEmailsForSendPin,
  setPinCheckError,
  resetPinCheckError,
  setEmailSources,
  setLoadingEmailFlow,
  cleanLoadingEmailFlow,
} from "./actions";
import {
  getSessionDetailById,
  pullListSessions,
  setSessionNotification,
} from "../sessions/actions";
import { replace } from "connected-react-router";
import { EmailFlowAction, EmailFlowActionTypes } from "./types";

const HOME_LINK = "/sessions/verify-pin/";

function* getEmailsEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    yield put(setLoadingEmailFlow());
    const requestData = {
      sessionId: payload.id,
    };
    const response = yield call(
      API.post,
      links.emailFlow.getContacts,
      requestData
    );
    const emailsData = response.data.contactItems;
    yield put(setEmailsForSendPin(emailsData));
  } catch (error) {
    yield generalErrorHandler(error);
  } finally {
    yield put(cleanLoadingEmailFlow());
  }
}
function* sendEmailEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    const { sessionId, emailId, email } = payload;
    const requestData = {
      sessionId,
      emailId,
    };
    yield call(API.post, links.emailFlow.sendMailPin, requestData);
    yield put(getEmailsForSendPin(sessionId));
    yield put(
      setSessionNotification(
        "success",
        `Support PIN was sent to ${email}.`,
        "check-light",
        Date.now()
      )
    );
  } catch (error) {
    yield generalErrorHandler(error);
  }
}
function* sendSmsEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    const { sessionId, maskedPhone } = payload;
    const requestData = {
      sessionId,
      maskedPhone,
    };
    yield call(API.post, links.emailFlow.sendSmsPin, requestData);
    yield put(getEmailsForSendPin(sessionId));
    yield put(
      setSessionNotification(
        "success",
        `Support PIN was sent to ${maskedPhone}.`,
        "check-light",
        Date.now()
      )
    );
  } catch (error) {
    yield generalErrorHandler(error);
  }
}
function* goToVerifyPinEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    yield put(setLoadingEmailFlow());
    const requestData = {
      sessionId: payload,
    };
    yield call(API.post, links.emailFlow.goToVerifyPin, requestData);
    yield put(getSessionDetailById(payload));
    yield put(pullListSessions());
  } catch (error) {
    yield generalErrorHandler(error);
  } finally {
    yield put(cleanLoadingEmailFlow());
  }
}
function* goBackToSecondStepEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    const requestData = {
      sessionId: payload,
    };
    yield call(API.post, links.emailFlow.goBackToSecondStep, requestData);
    yield put(getSessionDetailById(payload));
    yield put(pullListSessions());
  } catch (error) {
    yield generalErrorHandler(error);
  }
}

function* verifyPinEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    yield put(setLoadingEmailFlow());
    const requestData = {
      supportPin: payload.pin,
      sessionId: payload.sessionId,
    };
    const response = yield call(
      API.post,
      links.emailFlow.verifyPin,
      requestData
    );
    if (!response.data.isValid || response.data.isExpired) {
      yield put(setPinCheckError());
    } else {
      yield put(getSessionDetailById(payload.sessionId));
      yield put(pullListSessions());
    }
  } catch (error) {
    yield generalErrorHandler(error);
  } finally {
    yield put(cleanLoadingEmailFlow());
  }
}
function* backToFirstStepEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    yield call(API.post, links.session.cancel(payload.id));
    yield put(resetPinCheckError());
    yield put(pullListSessions());
    yield put(replace(`/sessions/verify-email?email=${payload.email}`));
  } catch (error) {
    yield generalErrorHandler(error);
  }
}

function* cancelSessionEmailFlowSaga({ payload }: EmailFlowAction) {
  try {
    yield call(API.post, links.session.cancel(payload));
    yield put(resetPinCheckError());
    yield put(pullListSessions());
    yield put(replace(HOME_LINK));
  } catch (error) {
    yield generalErrorHandler(error);
  }
}
function* getEmailSourcesSaga({ payload }: EmailFlowAction) {
  try {
    const requestData = {
      sessionId: payload,
    };
    const response = yield call(
      API.post,
      links.emailFlow.chooseSource,
      requestData
    );
    const { sessionSourcesToChoose, contactValue } = response.data;
    yield put(setEmailSources(sessionSourcesToChoose, contactValue));
  } catch (error) {
    yield generalErrorHandler(error);
  }
}
function* confirmEmailSourcesSaga({ payload }: EmailFlowAction) {
  try {
    yield put(setLoadingEmailFlow());
    yield call(API.post, links.emailFlow.setSource, payload);
    yield put(getSessionDetailById(payload.sessionId));
    yield put(pullListSessions());
  } catch (error) {
    yield generalErrorHandler(error);
  } finally {
    yield put(cleanLoadingEmailFlow());
  }
}
export default function* emailFlowSaga() {
  yield takeEvery(
    EmailFlowActionTypes.CANCEL_SESSION_EMAIL_FLOW,
    cancelSessionEmailFlowSaga
  );
  yield takeEvery(
    EmailFlowActionTypes.BACK_TO_FIRST_STEP_EMAIL_FLOW,
    backToFirstStepEmailFlowSaga
  );
  yield takeLatest(
    EmailFlowActionTypes.GET_EMAILS_FOR_EMAIL_FLOW,
    getEmailsEmailFlowSaga
  );
  yield takeLatest(
    EmailFlowActionTypes.SEND_EMAIL_FOR_EMAIL_FLOW,
    sendEmailEmailFlowSaga
  );
  yield takeLatest(EmailFlowActionTypes.GET_SOURCES, getEmailSourcesSaga);
  yield takeLatest(
    EmailFlowActionTypes.CONFIRM_SOURCES,
    confirmEmailSourcesSaga
  );

  yield takeLatest(
    EmailFlowActionTypes.SEND_SMS_FOR_EMAIL_FLOW,
    sendSmsEmailFlowSaga
  );
  yield takeEvery(
    EmailFlowActionTypes.VERIFY_PIN_EMAIL_FLOW,
    verifyPinEmailFlowSaga
  );
  yield takeEvery(
    EmailFlowActionTypes.GO_TO_VERIFY_PIN_EMAIL_FLOW,
    goToVerifyPinEmailFlowSaga
  );
  yield takeEvery(
    EmailFlowActionTypes.GO_BACK_TO_SECOND_STEP_EMAIL_FLOW,
    goBackToSecondStepEmailFlowSaga
  );
}
